import { useMemo, useState } from 'react';
import {
  BLACK,
  WHITE,
  formatVertex,
  ownership,
  starPoints,
  type Color,
  type Position,
  type Vertex,
} from '@baduk/engine';
import type { HoverQuality } from '@baduk/coach';
import { outlinePath } from '../outline.js';

/**
 * The board, drawn as SVG.
 *
 * SVG rather than canvas because every teaching overlay — liberty dots, group
 * highlights, territory shading, the coach's pointer — is just another element,
 * and because it stays crisp at any size on any screen.
 *
 * The overlays are handed in rather than worked out here. Whether a group is in
 * atari is a question about the rules, and that answer belongs in the engine the
 * bots and the coach already share; this file only decides what it looks like.
 */

/** A move the coach is proposing, ranked — 1 is its first choice. */
export interface Placement {
  readonly vertex: Vertex;
  readonly rank: number;
}

/** A group one move from being captured, and the point that decides it. */
export interface AtariMark {
  readonly color: Color;
  readonly stones: readonly Vertex[];
  readonly point: Vertex;
}

export interface EyeMark {
  readonly vertex: Vertex;
  readonly color: Color;
}

/** A group's liberty count, and the stone it is written on. */
export interface LibertyCount {
  readonly vertex: Vertex;
  readonly color: Color;
  readonly count: number;
}

/** One side of one stone that lies on the edge of its group. */
export interface ChainEdge {
  readonly vertex: Vertex;
  readonly side: 'top' | 'bottom' | 'left' | 'right';
}

/** The perimeter of a connected group. */
export interface ChainOutline {
  readonly color: Color;
  readonly edges: readonly ChainEdge[];
}

export interface BoardProps {
  readonly position: Position;
  readonly lastMove: Vertex | null;
  readonly toPlay: Color;
  readonly onPlay: (vertex: Vertex) => void;
  readonly legal: (vertex: Vertex) => boolean;
  /** Disable input while a bot is thinking or the game is over. */
  readonly disabled?: boolean;
  /** Points the coach wants to draw attention to. */
  readonly coachHighlight?: readonly Vertex[];
  /** The single point the coach is recommending, if it has given that away. */
  readonly coachMove?: Vertex | null;
  /** The coach's ranked shortlist, shown once it has given the answer away. */
  readonly candidates?: readonly Placement[];
  readonly showTerritory?: boolean;
  readonly showCoordinates?: boolean;
  /** Highlight the hovered group and its liberties — the core teaching aid. */
  readonly showLiberties?: boolean;
  /** Groups with one liberty left, from either side. */
  readonly ataris?: readonly AtariMark[];
  /** Points that are real eyes, so a learner can count them at a glance. */
  readonly eyes?: readonly EyeMark[];
  /** One number per group: how many liberties it has left. */
  readonly libertyCounts?: readonly LibertyCount[];
  /** Perimeters of the connected groups, drawn so connection is visible. */
  readonly outlines?: readonly ChainOutline[];
  /** Points the rules forbid to the player to move — ko and superko. */
  readonly forbidden?: readonly Vertex[];
  /** Stones agreed dead at the end of the game — off the board for counting. */
  readonly dead?: ReadonlySet<Vertex>;
  /** Set while marking dead stones: a click picks a group rather than plays. */
  readonly onMarkDead?: (vertex: Vertex) => void;
  /** Grade per point, for live feedback as the cursor moves. */
  readonly quality?: ReadonlyMap<Vertex, HoverQuality>;
  /** Reports the hovered point, so the feedback can be written out in words. */
  readonly onHoverChange?: (vertex: Vertex | null) => void;
}

const CELL = 30;
const MARGIN = 26;

export function Board({
  position,
  lastMove,
  toPlay,
  onPlay,
  legal,
  disabled = false,
  coachHighlight = [],
  coachMove = null,
  candidates = [],
  showTerritory = false,
  showCoordinates = true,
  showLiberties = true,
  ataris = [],
  eyes = [],
  libertyCounts = [],
  outlines = [],
  forbidden = [],
  dead,
  onMarkDead,
  quality,
  onHoverChange,
}: BoardProps) {
  const size = position.size;
  const [hover, setHover] = useState<Vertex | null>(null);

  // The board owns the hover because the overlays need it, but the feedback
  // panel needs it too, so every change is reported outwards as well.
  const moveHover = (vertex: Vertex | null) => {
    setHover(vertex);
    onHoverChange?.(vertex);
  };
  const hoverQuality = hover === null ? undefined : quality?.get(hover);
  const extent = MARGIN * 2 + CELL * (size - 1);

  const stars = useMemo(() => new Set(starPoints(size)), [size]);
  const territory = useMemo(
    () => (showTerritory ? ownership(position) : null),
    [showTerritory, position],
  );
  const highlighted = useMemo(() => new Set(coachHighlight), [coachHighlight]);
  const inAtari = useMemo(() => new Set(ataris.flatMap((mark) => [...mark.stones])), [ataris]);
  const labelled = useMemo(
    () => new Set(libertyCounts.map((mark) => mark.vertex)),
    [libertyCounts],
  );

  // Hovering a stone reveals its whole group and the liberties keeping it
  // alive. This one interaction teaches the most important idea in the game.
  const inspected = useMemo(() => {
    if (!showLiberties || hover === null || position.at(hover) === 0) return null;
    const group = position.group(hover);
    if (!group) return null;
    return { stones: new Set(group.stones), liberties: group.liberties };
  }, [hover, position, showLiberties]);

  const x = (v: Vertex) => MARGIN + (v % size) * CELL;
  const y = (v: Vertex) => MARGIN + Math.floor(v / size) * CELL;

  const labels = 'ABCDEFGHJKLMNOPQRSTUVWXYZ';

  return (
    <svg
      className="board"
      viewBox={`0 0 ${extent} ${extent}`}
      role="grid"
      aria-label={`${size} by ${size} Go board`}
      onMouseLeave={() => moveHover(null)}
    >
      <defs>
        {/*
          A highlight up and to the left on both colours, so the stones read as
          objects sitting on the board rather than as flat discs printed on it.
        */}
        <radialGradient id="stone-black" cx="35%" cy="32%" r="72%">
          <stop offset="0%" stopColor="#4a443c" />
          <stop offset="55%" stopColor="#1d1a16" />
          <stop offset="100%" stopColor="#0b0906" />
        </radialGradient>
        <radialGradient id="stone-white" cx="35%" cy="32%" r="72%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#f6f2e9" />
          <stop offset="100%" stopColor="#ddd6c6" />
        </radialGradient>
      </defs>

      <rect x={0} y={0} width={extent} height={extent} rx={6} className="board-bg" />

      {/* Territory shading sits under the grid so it reads as ground, not ink. */}
      {territory &&
        Array.from({ length: position.area }, (_, v) =>
          territory[v] !== 0 && position.at(v) === 0 ? (
            <rect
              key={`t${v}`}
              x={x(v) - CELL / 2}
              y={y(v) - CELL / 2}
              width={CELL}
              height={CELL}
              className={territory[v] === BLACK ? 'territory-black' : 'territory-white'}
            />
          ) : null,
        )}

      {/* Grid */}
      {Array.from({ length: size }, (_, i) => (
        <g key={`grid${i}`}>
          <line
            x1={MARGIN}
            y1={MARGIN + i * CELL}
            x2={MARGIN + (size - 1) * CELL}
            y2={MARGIN + i * CELL}
            className="grid-line"
          />
          <line
            x1={MARGIN + i * CELL}
            y1={MARGIN}
            x2={MARGIN + i * CELL}
            y2={MARGIN + (size - 1) * CELL}
            className="grid-line"
          />
        </g>
      ))}

      {[...stars].map((v) => (
        <circle key={`star${v}`} cx={x(v)} cy={y(v)} r={3} className="star-point" />
      ))}

      {showCoordinates &&
        Array.from({ length: size }, (_, i) => (
          <g key={`label${i}`} className="coordinate">
            <text x={MARGIN + i * CELL} y={12} textAnchor="middle">
              {labels[i]}
            </text>
            <text x={10} y={MARGIN + i * CELL + 4} textAnchor="middle">
              {size - i}
            </text>
          </g>
        ))}

      {/* Coach attention ring */}
      {[...highlighted].map((v) => (
        <circle key={`hl${v}`} cx={x(v)} cy={y(v)} r={CELL * 0.45} className="coach-highlight" />
      ))}

      {/* An eye drawn as what it is: a hole in a wall of a single colour. */}
      {eyes.map((eye) => (
        <circle
          key={`eye${eye.vertex}`}
          cx={x(eye.vertex)}
          cy={y(eye.vertex)}
          r={CELL * 0.19}
          className={`eye-mark ${eye.color === BLACK ? 'eye-black' : 'eye-white'}`}
        />
      ))}

      {/*
        The perimeter of each connected group, as one path rather than a line per
        edge — a full 19x19 board would otherwise add well over a thousand
        elements to the drawing.
      */}
      {outlines.map((outline) => (
        <path
          key={`out${outline.edges[0]?.vertex ?? 0}-${outline.color}`}
          d={outlinePath(outline, CELL, x, y)}
          className={`chain-outline ${outline.color === BLACK ? 'outline-black' : 'outline-white'}`}
        />
      ))}

      {/* Liberties of the hovered group */}
      {inspected?.liberties.map((v) => (
        <circle key={`lib${v}`} cx={x(v)} cy={y(v)} r={5} className="liberty-dot" />
      ))}

      {/* Stones */}
      {Array.from({ length: position.area }, (_, v) => {
        const stone = position.at(v);
        if (stone === 0) return null;
        return (
          <circle
            key={`s${v}`}
            cx={x(v)}
            cy={y(v)}
            r={CELL * 0.46}
            className={[
              'stone',
              stone === BLACK ? 'stone-black' : 'stone-white',
              inspected?.stones.has(v) ? 'stone-inspected' : '',
              inAtari.has(v) ? 'stone-atari' : '',
              dead?.has(v) ? 'stone-dead' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        );
      })}

      {/* The point that settles each atari — one move from a capture. */}
      {ataris.map((mark) => (
        <g key={`atari${mark.point}-${mark.stones[0]}`} className="atari-point">
          <circle cx={x(mark.point)} cy={y(mark.point)} r={CELL * 0.25} />
          <text x={x(mark.point)} y={y(mark.point) + 4} textAnchor="middle">
            !
          </text>
        </g>
      ))}

      {/* Forbidden by ko: the one shape where a legal-looking move is not. */}
      {forbidden.map((v) => (
        <g key={`ko${v}`} className="forbidden-point">
          <line x1={x(v) - 6} y1={y(v) - 6} x2={x(v) + 6} y2={y(v) + 6} />
          <line x1={x(v) - 6} y1={y(v) + 6} x2={x(v) + 6} y2={y(v) - 6} />
        </g>
      ))}

      {/*
        The last move is normally a small ring in the middle of the stone, which
        is exactly where a liberty count goes. Where the two would land on the
        same stone the ring opens out to the edge of it instead, so the number
        stays readable inside.
      */}
      {lastMove !== null && position.at(lastMove) !== 0 && (
        <circle
          cx={x(lastMove)}
          cy={y(lastMove)}
          r={labelled.has(lastMove) ? CELL * 0.38 : 5}
          className="last-move"
        />
      )}

      {/*
        Stones counted as dead. Faded rather than removed, because they are still
        physically on the board and the player may disagree with the marking.
      */}
      {dead &&
        [...dead].map((v) =>
          position.at(v) === 0 ? null : (
            <g key={`dead${v}`} className="dead-mark">
              <line x1={x(v) - 6} y1={y(v) - 6} x2={x(v) + 6} y2={y(v) + 6} />
              <line x1={x(v) - 6} y1={y(v) + 6} x2={x(v) + 6} y2={y(v) - 6} />
            </g>
          ),
        )}

      {/* One number per group, written on the stone nearest its middle. */}
      {libertyCounts.map((mark) => (
        <text
          key={`lc${mark.vertex}`}
          x={x(mark.vertex)}
          y={y(mark.vertex) + 4}
          textAnchor="middle"
          className={`liberty-count ${mark.color === BLACK ? 'on-black' : 'on-white'}`}
        >
          {mark.count}
        </text>
      ))}

      {/*
        The coach's shortlist, ranked. Its first choice keeps the dashed ring it
        always had; the rest are numbered, so the advice can be read off the board
        rather than translated out of a paragraph one coordinate at a time.
      */}
      {candidates.map((placement) => (
        <g
          key={`cand${placement.vertex}`}
          className={`candidate${placement.rank === 1 ? ' candidate-best' : ''}`}
        >
          <circle cx={x(placement.vertex)} cy={y(placement.vertex)} r={CELL * 0.34} />
          <text x={x(placement.vertex)} y={y(placement.vertex) + 4} textAnchor="middle">
            {placement.rank}
          </text>
        </g>
      ))}

      {coachMove !== null && (
        <circle cx={x(coachMove)} cy={y(coachMove)} r={CELL * 0.4} className="coach-move" />
      )}

      {/*
        Live grade for the point under the cursor. Drawn as a ring rather than a
        fill so it reads on an empty intersection and on an illegal one alike —
        the illegal ones are the reason this is not folded into the ghost stone,
        which only appears where a move can actually be played.
      */}
      {hover !== null && hoverQuality && position.at(hover) === 0 && (
        <circle
          cx={x(hover)}
          cy={y(hover)}
          r={CELL * 0.46}
          className={`hover-grade grade-${hoverQuality}`}
        />
      )}

      {/* Ghost stone under the cursor, only where a move is actually legal. */}
      {!disabled && hover !== null && position.at(hover) === 0 && legal(hover) && (
        <circle
          cx={x(hover)}
          cy={y(hover)}
          r={CELL * 0.46}
          className={`ghost ${toPlay === BLACK ? 'stone-black' : 'stone-white'}`}
        />
      )}

      {/* Invisible hit targets, one per intersection. */}
      {Array.from({ length: position.area }, (_, v) => (
        <rect
          key={`hit${v}`}
          x={x(v) - CELL / 2}
          y={y(v) - CELL / 2}
          width={CELL}
          height={CELL}
          className="hit-target"
          role="gridcell"
          aria-label={`${formatVertex(size, v)}${
            position.at(v) === BLACK ? ', black stone' : position.at(v) === WHITE ? ', white stone' : ''
          }`}
          onMouseEnter={() => moveHover(v)}
          onClick={() => {
            // Marking the dead takes over the board once the game is over: the
            // same click that played a stone now settles whether one lived.
            if (onMarkDead) {
              if (position.at(v) !== 0) onMarkDead(v);
              return;
            }
            if (!disabled && position.at(v) === 0 && legal(v)) onPlay(v);
          }}
        />
      ))}
    </svg>
  );
}
